import { useMemo, useRef, useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle, LineChart, StackedBarChart } from '@mesh.ai/rabbit';
import OverviewSection from 'Deer/components/Overview/OverviewSection';
import { useGetOverviewMetrics } from 'Deer/hooks/dataHooks/metrics/useGetOverviewMetrics';
import { useGetOverviewMetricsExplanations } from 'Deer/hooks/dataHooks/metrics/useGetOverviewMetricsExplanations';
import useLocalStorageCards from 'Deer/hooks/useLocalStorageCards';
import { MetricCards } from 'Deer/metricCards/MetricCards';
import { setIsCardOpen } from 'Deer/slice/DeerCollapsibleCard.slice';
import { useSelectVisierAuthToken } from 'Deer/slice/UserAccessesSlice';
import { format } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { checkSandboxStaticDataFeaturesEnabled, checkNewStaticDataFeaturesEnabled } from 'utils/helpers/featureFlags';
import { getUniqueCardKey } from 'utils/helpers/getUniqueCardKey';

import { transformedCardsForCompetencies } from '../../utlis';
import { DeerBarChart } from '../common/DeerBarChart';
import { DeerLineChart } from '../common/DeerLineChart';
import { staticBarChartData, staticCardsData, staticLineChartData } from './feedbackMockData';

export const DirectReportsFeedbackOverview = () => {
  const history = useHistory();
  const visierAuthToken = useSelectVisierAuthToken();
  const dispatch = useDispatch();
  const currentURL = history.location.pathname;
  const barChartRef = useRef();
  const lineChartRef = useRef();
  const localStorageKey = getUniqueCardKey({ url: currentURL, cardId: 'myDirectTeamCompetenciesOverview' });

  const isCardOpen = useLocalStorageCards({
    localStorageKey,
    card: 'competenciesOverview',
    section: 'myDirectTeam',
  });

  const currentDate = new Date();

  const [correlationId] = useState(window.crypto.randomUUID());

  const formattedDate = format(currentDate, 'yyyy-MM-dd');

  const { data, isLoading, isError, isFetched } = useGetOverviewMetrics<
    ['COMPETENCY_SCORE_AVERAGE_BY_COMPETENCY', 'COMPETENCY_PROGRESS_AVERAGE']
  >({
    metrics: [
      {
        analyticsType: 'CORE_COMPETENCY',
        metricType: 'COMPETENCY_SCORE_AVERAGE_BY_COMPETENCY',
        params: {
          mock: false,
          time: {
            endDate: formattedDate,
            intervalCount: 6,
            periodCount: 1,
            periodType: 'MONTH',
          },
          token: visierAuthToken,
        },
      },
      {
        analyticsType: 'CORE_COMPETENCY',
        metricType: 'COMPETENCY_PROGRESS_AVERAGE',
        params: {
          mock: false,
          time: {
            endDate: formattedDate,
            intervalCount: 6,
            periodCount: 1,
            periodType: 'MONTH',
          },
          token: visierAuthToken,
        },
      },
    ],
    cohortId: -2,
    group: 'FEEDBACK',
    correlationId,
  });

  const { data: kpiCardData } = useGetOverviewMetrics<
    ['COMPETENCY_PROGRESS_AVERAGE_KPI', 'EMP_COUNT_COMPETENCIES_NO_PROGRESS', 'EMP_COUNT_NO_FEEDBACK_RECEIVED']
  >({
    metrics: [
      {
        analyticsType: 'CORE_COMPETENCY',
        metricType: 'COMPETENCY_PROGRESS_AVERAGE_KPI',
        params: {
          mock: false,
          time: {
            endDate: formattedDate,
            intervalCount: 1,
            periodCount: 1,
            periodType: 'MONTH',
          },
          token: visierAuthToken,
        },
      },
      {
        analyticsType: 'CORE_COMPETENCY',
        metricType: 'EMP_COUNT_COMPETENCIES_NO_PROGRESS',
      },
      {
        analyticsType: 'CORE_COMPETENCY',
        metricType: 'EMP_COUNT_NO_FEEDBACK_RECEIVED',
      },
    ],
    cohortId: -2,
  });

  const barChartData = useMemo(() => data && data.COMPETENCY_SCORE_AVERAGE_BY_COMPETENCY, [data]);

  const lineChartData = useMemo(() => data && data.COMPETENCY_PROGRESS_AVERAGE, [data]);

  const cardsDataFromApi = useMemo(
    () =>
      kpiCardData &&
      transformedCardsForCompetencies({
        COMPETENCY_PROGRESS_AVERAGE_KPI: kpiCardData.COMPETENCY_PROGRESS_AVERAGE_KPI,
        EMP_COUNT_COMPETENCIES_NO_PROGRESS: kpiCardData.EMP_COUNT_COMPETENCIES_NO_PROGRESS,
        EMP_COUNT_NO_FEEDBACK_RECEIVED: kpiCardData.EMP_COUNT_NO_FEEDBACK_RECEIVED,
      }),
    [kpiCardData]
  );

  const { data: explanationData } = useGetOverviewMetricsExplanations({
    cohortId: -2,
    group: 'FEEDBACK',
    correlationId,
    enabled: isFetched,
  });

  const explanation: {
    explanation?: string;
    sentiment?: 'warning' | 'success';
  } = useMemo(
    () =>
      explanationData && explanationData[0]
        ? {
            explanation: explanationData[0].explanation,
            sentiment: explanationData[0].sentiment === 'negative' ? 'warning' : 'success',
          }
        : {},
    [explanationData]
  );

  const handleChange = (newVal: boolean) => {
    dispatch(
      setIsCardOpen({
        card: 'competenciesOverview',
        section: 'myDirectTeam',
        isCardOpen: newVal,
        localStorageKey,
      })
    );
    return newVal;
  };

  return (
    <OverviewSection
      isError={
        checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() ? false : !visierAuthToken
      }
      open={isCardOpen}
      onOpenChange={handleChange}
      title="Competencies"
      showCaption={
        checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() || explanation.explanation
      }
      subTitleColor={explanation.sentiment ?? 'warning'}
      subTitle={
        explanation.explanation ??
        "Your team's competency progress this cycle needs attention. Ming shows early promise, but Jay seem to be struggling. Consider creating individual development plans with clear competency-focused initiatives."
      }
      topSection={
        <MetricCards
          columns={`repeat(${
            checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled()
              ? staticCardsData.length
              : cardsDataFromApi?.length
          }, 1fr)`}
          cards={
            checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled()
              ? staticCardsData
              : cardsDataFromApi
          }
          cardTitle="Metrics"
        />
      }
      emptyStateDescription="Insights on your team's relative performance would appear here over time and with check-ins."
    >
      {/* Competency Distribution  Chart */}
      <Card
        style={{
          marginBottom: '8px',
        }}
      >
        <CardHeader>
          <CardTitle>Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          {checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() ? (
            <div style={{ height: '240px' }} className="rabbit-ds">
              <StackedBarChart
                ref={barChartRef}
                mode="full"
                nodes={staticBarChartData.nodes}
                series={staticBarChartData?.series}
                tooltipTitle="Distribution"
                xAxisLabel="Team members"
                xAxisValues={staticBarChartData?.xaxis}
                yAxisLabel="Average proficiency"
                yAxisUnitSymbol={{
                  position: 'post',
                  symbol: '%',
                }}
                seriesSelectorTitle="Competencies"
                maxNodesInMiniMode={0}
              />
            </div>
          ) : (
            <DeerBarChart
              ref={barChartRef}
              tooltipTitle="Distribution"
              xAxisLabel="Team members"
              yAxisLabel="Average proficiency"
              seriesSelectorTitle="Status"
              series={barChartData?.series}
              xAxisValues={barChartData?.xAxis}
              isLoading={isLoading}
              isError={isError}
              nodes={barChartData?.nodes}
              hasNoData={barChartData?.xAxis.length === 0}
              emptyStateDescription="Insights on your team's relative performance would appear here over time and with check-ins."
            />
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Proficiency</CardTitle>
        </CardHeader>
        <CardContent>
          {checkSandboxStaticDataFeaturesEnabled() || checkNewStaticDataFeaturesEnabled() ? (
            <div style={{ height: '222px' }} className="rabbit-ds">
              <LineChart
                ref={lineChartRef}
                nodes={staticLineChartData?.nodes}
                xAxisValues={staticLineChartData?.xaxis}
                series={staticLineChartData?.series}
                xAxisLabel="Months"
                yAxisLabel="Proficiency"
                mode="full"
                connectNulls
                tooltipTitle="Progress"
                seriesSelectorTitle="Team members"
                maxNodesInMiniMode={0}
                yAxisUnitSymbol={{
                  position: 'post',
                  symbol: '%',
                }}
              />
            </div>
          ) : (
            <DeerLineChart
              ref={lineChartRef}
              xAxisLabel="Months"
              yAxisLabel="Proficiency"
              tooltipTitle="Progress"
              seriesSelectorTitle="Team members"
              series={lineChartData?.series}
              xAxisValues={lineChartData?.xAxis}
              isLoading={isLoading}
              isError={isError}
              nodes={lineChartData?.nodes}
              hasNoData={lineChartData?.series.length === 0}
              emptyStateDescription="Insights on your team's relative performance would appear here over time and with check-ins."
            />
          )}
        </CardContent>
      </Card>
    </OverviewSection>
  );
};
